import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLines, Reveal, ClipImage } from "./Reveal";
import { IMG } from "../data/content";

export const BrandStatement = () => {
  const secRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: secRef, offset: ["start end", "end start"] });
  const yImg1 = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const yImg2 = useTransform(scrollYProgress, [0, 1], [110, -70]);
  const yText = useTransform(scrollYProgress, [0, 1], [30, -30]);

  return (
    <section ref={secRef} data-testid="brand-statement" className="px-6 sm:px-10 lg:px-16 pt-10 sm:pt-16 pb-16 sm:pb-36 max-w-[1700px] mx-auto overflow-visible">
      <div className="flex items-baseline justify-between mb-10 sm:mb-14">
        <p className="font-mono text-[11px] sm:text-xs tracking-[0.28em] text-ember">(ABOUT NAGEL)</p>
        <p className="hidden sm:block font-mono text-[11px] tracking-[0.22em] text-charcoal/40">EST. 1972 — CALIFORNIA</p>
      </div>

      <MaskLines
        as="h2"
        lines={[
          <><span className="font-semibold">We don't just maintain</span> landscapes.</>,
          <>We build <span className="font-serif italic font-normal">the places</span> around them.</>,
        ]}
        className="font-display font-light tracking-[-0.03em] leading-[1.04] text-4xl sm:text-6xl lg:text-[5.2rem] text-charcoal max-w-6xl"
      />

      <div className="mt-20 lg:mt-32 grid lg:grid-cols-12 gap-8 lg:gap-10 items-start">
        <motion.div style={{ y: yImg1 }} className="lg:col-span-5">
          <ClipImage
            src={IMG.brandTall}
            alt="Garden pathway landscape by Nagel"
            className="aspect-[3/4] w-full"
            parallax={7}
          />
          <p className="mt-4 font-mono text-[11px] tracking-[0.22em] text-charcoal/40">GARDEN PATHWAYS &amp; PLANTING</p>
        </motion.div>

        <motion.div style={{ y: yText }} className="lg:col-span-4 lg:pt-40 space-y-7">
          <Reveal>
            <p className="text-lg sm:text-2xl font-medium text-charcoal leading-snug tracking-[-0.01em]">
              A landscape is more than what surrounds a building.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-sm sm:text-base text-charcoal/60 leading-relaxed">
              It shapes the first impression, the way people move through a property, the way a space feels — and how it performs years after the project is finished.
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="text-sm sm:text-base text-charcoal/60 leading-relaxed">
              For more than five decades, Nagel Landscaping has been creating outdoor environments for businesses, communities and homeowners throughout Northern and Central California.
            </p>
          </Reveal>
          <Reveal delay={0.26}>
            <p className="font-display font-semibold tracking-[-0.02em] text-2xl sm:text-3xl text-ember leading-tight">
              One team. From concept to completion.
            </p>
          </Reveal>
        </motion.div>

        <motion.div style={{ y: yImg2 }} className="lg:col-span-3 lg:pt-64">
          <ClipImage
            src={IMG.brandSmall}
            alt="Landscape planting detail"
            className="aspect-[4/5] w-full"
            parallax={9}
            rounded="rounded-[2rem]"
          />
          <p className="mt-4 font-mono text-[11px] tracking-[0.22em] text-charcoal/40">PLANTING &amp; DETAIL</p>
        </motion.div>
      </div>
    </section>
  );
};
